import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@pella/sash-framework';
import { Helmet } from 'react-helmet';
import styles from './pagination.module.scss';
import usePagination from '../../../../hooks/use-pagination';
import { useMediaQuery } from 'react-responsive';

const Pagination = (props) => {
	const { currentPage, setCurrentPage, pageCount, title } = usePagination({
		tiles: props.tiles,
		tilesPerPage: props.tilesPerPage,
		seoTitle: props.seo.title,
	});
	const [currentTiles, setCurrentTiles] = props.currentTiles;

	const isMobile = useMediaQuery({ query: '(max-width: 549px)' } ?? false);

	let startPage = 1;
	let endPage = pageCount;

	// calculate start and end item indexes
	let startIndex = (currentPage - 1) * props.tilesPerPage;
	let endIndex = Math.min(startIndex + props.tilesPerPage - 1, props.tiles.length - 1);

	useEffect(() => {
		const sortPriceLowHigh = (items) =>
			items.sort(function (a, b) {
				if (b?.price?.consumer?.amount === a?.price?.consumer?.amount) {
					return 0;
				} else if (a?.price?.consumer?.amount == undefined || a?.price?.consumer?.amount > b?.price?.consumer?.amount) {
					return 1;
				} else if (b?.price?.consumer?.amount == undefined || a?.price?.consumer?.amount < b?.price?.consumer?.amount) {
					return -1;
				}

				setCurrentTiles(items);
			});

		const sortPriceHighLow = (items) =>
			items.sort(function (b, a) {
				if (b?.price?.consumer?.amount === a?.price?.consumer?.amount) {
					return 0;
				} else if (b?.price?.consumer?.amount == undefined || a?.price?.consumer?.amount > b?.price?.consumer?.amount) {
					return 1;
				} else if (a?.price?.consumer?.amount == undefined || a?.price?.consumer?.amount < b?.price?.consumer?.amount) {
					return -1;
				}

				setCurrentTiles(items);
			});

		if (props.tileSort === 'recommended') {
			let tempArray = props.tiles;

			tempArray?.forEach((tile, index) => {
				if (props.highlightItems.find((item) => item === tile.backend_name)) {
					tempArray.splice(index, 1);
					tempArray.unshift(tile);
				}
			});

			setCurrentTiles(tempArray.slice(startIndex, endIndex + 1));
		} else if (props.tileSort === 'price-low-high') {
			setCurrentTiles(sortPriceLowHigh(props.tiles).slice(startIndex, endIndex + 1));
		} else if (props.tileSort === 'price-high-low') {
			setCurrentTiles(sortPriceHighLow(props.tiles).slice(startIndex, endIndex + 1));
		}
	}, [endIndex, props.highlightItems, props.tileSort, props.tiles, setCurrentTiles, startIndex]);

	let paginationButtonCount = isMobile ? 4 : 10;

	if (pageCount <= paginationButtonCount) {
		// less than 4 or 10 total pages, show all
		startPage = 1;
		endPage = pageCount;
	} else {
		// more than 4 or 10 total pages, calculate start and end pages
		let a = isMobile ? 3 : 6;
		let b = isMobile ? 1 : 4;
		if (currentPage <= a) {
			startPage = 1;
			endPage = isMobile ? 4 : 10;
		} else if (currentPage + b >= pageCount) {
			let x = isMobile ? 3 : 9;
			startPage = pageCount - x;
			endPage = pageCount;
		} else {
			let y = isMobile ? 2 : 5;
			let z = isMobile ? 1 : 4;
			startPage = currentPage - y;
			endPage = currentPage + z;
		}
	}

	function prev() {
		setCurrentPage((currentPage) => Math.max(currentPage - 1, 1));

		const parent = document.getElementById('js-plpResults');
		if (parent) setTimeout(() => window.scroll({ top: parent.offsetTop - 50 }), 0);
	}

	function jump(page) {
		const pageNumber = Math.max(1, page);
		setCurrentPage(() => Math.min(pageNumber, pageCount));

		const parent = document.getElementById('js-plpResults');
		if (parent) setTimeout(() => window.scroll({ top: parent.offsetTop - 50 }), 0);
	}

	function next() {
		setCurrentPage((currentPage) => Math.min(currentPage + 1, pageCount));

		const parent = document.getElementById('js-plpResults');
		if (parent) setTimeout(() => window.scroll({ top: parent.offsetTop - 50 }), 0);
	}

	const pages = [...Array(endPage + 1 - startPage).keys()].map((i) => startPage + i);

	if (!pages || pages.length <= 1) return null;

	return (
		<>
			<Helmet>
				<title>{title}</title>
			</Helmet>
			<ul className={styles.pagination}>
				<li>
					{currentPage > 1 ? (
						<a
							href={`${window.location.origin}${window.location.pathname}${currentPage - 1 > 1 ? `?page=${currentPage - 1}` : ''}`}
							onClick={(e) => {
								e.preventDefault();
								prev();
							}}
							aria-label='Previous'
							className={styles.paginationPrev}
							rel='prev'
						>
							<Icon name='keyboard_arrow_left' />
						</a>
					) : (
						<span className={`${styles.paginationPrev} ${styles.paginationDisabled}`}>
							<Icon name='keyboard_arrow_left' />
						</span>
					)}
				</li>
				{pages.map((page, index) => (
					<li key={index}>
						<a
							href={`${window.location.origin}${window.location.pathname}${page > 1 ? `?page=${page}` : ''}`}
							onClick={(e) => {
								e.preventDefault();
								jump(page);
							}}
							aria-label={`Jump to page ${page}`}
							className={`${styles.paginationPage} ${currentPage === page ? styles.paginationPage_active : ''}`}
						>
							{page}
						</a>
					</li>
				))}
				<li>
					{currentPage < pageCount ? (
						<a
							href={`${window.location.origin}${window.location.pathname}?page=${currentPage + 1}`}
							onClick={(e) => {
								e.preventDefault();
								next();
							}}
							aria-label='Next'
							className={styles.paginationNext}
							rel='next'
						>
							<Icon name='keyboard_arrow_right' />
						</a>
					) : (
						<span className={`${styles.paginationNext} ${styles.paginationDisabled}`}>
							<Icon name='keyboard_arrow_right' />
						</span>
					)}
				</li>
			</ul>
		</>
	);
};

Pagination.propTypes = {
	isMobile: PropTypes.bool,
	tiles: PropTypes.array,
	tilesPerPage: PropTypes.number,
	currentTiles: PropTypes.array,
	tileSort: PropTypes.string,
	highlightItems: PropTypes.array,
	seo: PropTypes.object,
};

export default Pagination;
